import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { Card, CardContent } from '@connected/ui/components/card';
import 'react-phone-number-input/style.css';
import { Popout, PopoutContent, PopoutDescription, PopoutTitle } from '@connected/ui/components/popout';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LockKeyhole, Mail } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';

export const Route = createFileRoute(
    '/_auth/dashboard/account/$userId/security/',
)({
    component: RouteComponent,
});
type ChangePasswordModalProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
};

type ChangeEmailModalProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    currentEmail: string;
};

type ChangeAccountPasswordProps = components['schemas']['ChangeAccountPasswordV2Request'];

type ChangeAccountEmailProps = components['schemas']['ChangeAccountEmailV2Request'] & {
    CurrentEmail: string;
    CheckEmail: string;
};

type ChangePasswordWithConfirm = ChangeAccountPasswordProps & {
    PasswordConfirm: string;
};

function EmailChangeModal({ isOpen, setIsOpen, currentEmail }: ChangeEmailModalProps) {
    const { t } = useTranslation('dashboard');

    const DEFAULT_VALUES_CHANGE_PASSWORD: ChangeAccountEmailProps = {
        CurrentEmail: '',
        Email: '',
        Password: '',
        CheckEmail: '',
    };
    const navigate = useNavigate();
    const emailUserRequest = api.useMutation(
        'post',
        '/api/v2/accounts/emails/change',
        {
            onSuccess: () => {
                toast.success(t('dashboard.account.email.error.succesfullyChanged'));
                setIsOpen(false);
                navigate({
                    to: '/logout',
                    search: {},
                    replace: true,
                });
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'UpdateAccountSettings',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/accounts/passwords/change' },
                );

                toast.error(
                    t(error?.Message ?? t('dashboard.general.messages.error')),
                );
            },
        },
    );
    const changePasswordForm = useAppForm({
        defaultValues: DEFAULT_VALUES_CHANGE_PASSWORD,
        onSubmit: async ({ value }) => {
            if (currentEmail && value.CurrentEmail !== currentEmail) {
                toast.error(t('dashboard.account.email.error.noEmailPassword'));
                return;
            }
            if (value.CheckEmail !== value.Email) {
                toast.error(t('dashboard.account.email.error.noNewEmailMatch'));
                return;
            }
            await emailUserRequest.mutateAsync({
                body: {
                    Email: value.Email,
                    Password: value.Password,
                },
            });
        },
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        changePasswordForm.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent asChild>
                <div className="max-w-md w-full mx-auto bg-white rounded-lg shadow-xl">
                    <div className="flex flex-col gap-5 m-10">
                        <div className="flex items-center justify-center">
                            <div className="p-3 rounded border border-gray-200">
                                <Mail />
                            </div>
                        </div>
                        <PopoutTitle>{t('dashboard.account.email.edit.title')}</PopoutTitle>
                        <div>

                            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-1">
                                <changePasswordForm.AppForm>
                                    {currentEmail
                                        ? (
                                                <changePasswordForm.AppField
                                                    name="CurrentEmail"
                                                    validators={{
                                                        onChange: validators.changeUserEmailCommand.email,
                                                    }}
                                                    children={field => (
                                                        <field.Label>
                                                            <field.Label.Title label={t('dashboard.account.email.edit.table.currentEmail')} />
                                                            <field.Input type="email" placeholder={t('dashboard.account.password.edit.table.currentEmailPlaceholder')} />
                                                            <field.ValidationError />
                                                        </field.Label>
                                                    )}
                                                />
                                            )
                                        : ''}
                                    <changePasswordForm.AppField
                                        name="Password"
                                        validators={{
                                            onChange: validators.changeAccountPasswordV2Request.currentPassword,
                                        }}
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.account.password.edit.table.oldPassword')} />
                                                <field.Input type="password" placeholder={t('dashboard.account.password.edit.table.oldPasswordPlaceholder')} />
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                    <br />
                                    <changePasswordForm.AppField
                                        name="Email"
                                        validators={{
                                            onChange: validators.changeUserEmailCommand.email,
                                        }}
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.account.email.edit.table.newEmail')} />
                                                <field.Input type="email" placeholder={t('dashboard.account.email.edit.table.newEmailPlaceholder')} />
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                    <changePasswordForm.AppField
                                        name="CheckEmail"
                                        validators={{
                                            onChange: validators.changeUserEmailCommand.email,
                                        }}
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.account.email.edit.table.confirmEmail')} />
                                                <field.Input type="email" placeholder={t('dashboard.account.email.edit.table.newEmailConfirmPlaceholder')} />
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                    <changePasswordForm.SubmitButton asChild>
                                        <Button type="submit" className="bg-primary w-full mt-5">{t('dashboard.save')}</Button>
                                    </changePasswordForm.SubmitButton>
                                </changePasswordForm.AppForm>
                            </form>

                        </div>
                        <div>
                            <Button variant="ghost" onClick={() => setIsOpen(false)} className="font-semibold text-xs w-full">{t('dashboard.cancel')}</Button>
                        </div>
                    </div>
                </div>
            </PopoutContent>
        </Popout>
    );
}

function PasswordChangeModal({ isOpen, setIsOpen }: ChangePasswordModalProps) {
    const { t } = useTranslation('dashboard');

    const DEFAULT_VALUES_CHANGE_PASSWORD: ChangePasswordWithConfirm = {
        CurrentPassword: '',
        NewPassword: '',
        PasswordConfirm: '',
    };

    const resetUserPassword = api.useMutation(
        'post',
        '/api/v2/accounts/passwords/change',
        {
            onSuccess: () => {
                toast.success(t('dashboard.account.password.error.succesfullyChanged'));
                setIsOpen(false);
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'UpdateAccountSettings',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/accounts/passwords/change' },
                );

                toast.error(
                    t(error?.Message ?? t('dashboard.general.messages.error')),
                );
            },
        },
    );
    const changePasswordForm = useAppForm({
        defaultValues: DEFAULT_VALUES_CHANGE_PASSWORD,
        onSubmit: async ({ value }) => {
            if (value.NewPassword !== value.PasswordConfirm) {
                toast.error(t('dashboard.account.password.error.noMatchPassword'));
                return;
            }
            await resetUserPassword.mutateAsync({
                body: {
                    CurrentPassword: value.CurrentPassword,
                    NewPassword: value.NewPassword,
                },
            });
        },
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        changePasswordForm.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent asChild>
                <div className="max-w-md w-full mx-auto bg-white rounded-lg shadow-xl">
                    <div className="flex flex-col gap-5 m-10">
                        <div className="flex items-center justify-center">
                            <div className="p-3 rounded border border-gray-200">
                                <LockKeyhole />
                            </div>
                        </div>
                        <PopoutTitle>{t('dashboard.account.password.edit.title')}</PopoutTitle>
                        <PopoutDescription>{t('dashboard.account.password.edit.subtitle')}</PopoutDescription>
                        <div>

                            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-1">
                                <changePasswordForm.AppForm>
                                    <changePasswordForm.AppField
                                        name="CurrentPassword"
                                        validators={{
                                            onChange: validators.changeAccountPasswordV2Request.currentPassword,
                                        }}
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.account.password.edit.table.oldPassword')} />
                                                <field.Input type="password" placeholder={t('dashboard.account.password.edit.table.oldPasswordPlaceholder')} />
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                    <changePasswordForm.AppField
                                        name="NewPassword"
                                        validators={{
                                            onChange: validators.changeAccountPasswordV2Request.newPassword,
                                        }}
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.account.password.edit.table.newPassword')} />
                                                <field.Input type="password" placeholder={t('dashboard.account.password.edit.table.newPasswordPlaceholder')} />
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                    <changePasswordForm.AppField
                                        name="PasswordConfirm"
                                        validators={{
                                            onChange: validators.changeAccountPasswordV2Request.newPassword,
                                        }}
                                        children={field => (
                                            <field.Label>
                                                <field.Label.Title label={t('dashboard.account.password.edit.table.confirmPassword')} />
                                                <field.Input type="password" placeholder={t('dashboard.account.password.edit.table.newPasswordPlaceholder')} />
                                                <field.ValidationError />
                                            </field.Label>
                                        )}
                                    />
                                    <changePasswordForm.SubmitButton asChild>
                                        <Button type="submit" className="bg-primary w-full mt-5">{t('dashboard.save')}</Button>
                                    </changePasswordForm.SubmitButton>
                                </changePasswordForm.AppForm>
                            </form>

                        </div>
                        <div>
                            <Button variant="ghost" onClick={() => setIsOpen(false)} className="font-semibold text-xs w-full">{t('dashboard.cancel')}</Button>
                        </div>
                    </div>
                </div>
            </PopoutContent>
        </Popout>
    );
}
export function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);
    const [isEmailChangeModalOpen, setIsEmailChangeModalOpen] = useState(false);
    const auth = useAuthentication();

    const DEFAULT_VALUES = {
        Email: '',
        Password: '',
    };
    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
    });

    const accountList = useQuery(elektraAppQueryOptions.organisation.management.usersListOptions({
        organisationId: auth.currentUser?.Organization?.OrganizationId ?? '',
    }));

    const userEmail = useMemo(() => {
        if (accountList?.isFetched && accountList?.isSuccess) {
            return accountList?.data?.Users?.find(x => x.UserId === auth.currentUser?.UserId)?.Email;
        }
        return null;
    }, [accountList?.data?.Users, accountList?.isFetched, accountList?.isSuccess, auth.currentUser?.UserId]);

    return (
        <div className="flex flex-col gap-10">
            <div className="flex w-full flex-col lg:flex-row gap-6">
                <div className="w-full md:basis-1/4">
                    <p className="font-semibold text-sm">{t('dashboard.users.table.email')}</p>
                    <p className="text-sm">{t('dashboard.account.email.subtitle')}</p>
                </div>
                <div className="md:basis-2/4 w-full">
                    <Card>
                        <CardContent className="mt-6 ml-1 mr-1">
                            <form.AppField
                                name="Email"
                                children={field => (
                                    <field.Label>
                                        <field.Label.Title label={t('dashboard.users.table.email')} />
                                        <field.Input
                                            disabled
                                            placeholder={
                                                userEmail?.replace(
                                                    /(^.{3})|(@.{2})|[^@.]/g,
                                                    (_unused, p1, p2) => p1 || p2 || '*',
                                                ) || '********'
                                            }
                                        />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            >

                            </form.AppField>
                            <div className="flex justify-end">
                                <Button
                                    onClick={() => setIsEmailChangeModalOpen(true)}
                                    variant="ghost"
                                    className="font-semibold text-sm"
                                >
                                    {t('dashboard.edit')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                { isEmailChangeModalOpen
                    ? (
                            <EmailChangeModal currentEmail={userEmail ?? ''} isOpen={isEmailChangeModalOpen} setIsOpen={setIsEmailChangeModalOpen} />

                        )
                    : null }
            </div>
            <div className="flex w-full flex-col lg:flex-row gap-6">
                <div className="w-full md:basis-1/4">
                    <p className="font-semibold text-sm">{t('dashboard.users.table.password')}</p>
                    <p className="text-sm">{t('dashboard.account.password.subtitle')}</p>
                </div>
                <div className="md:basis-2/4 w-full">
                    <Card>
                        <CardContent className="mt-6 ml-1 mr-1">
                            <form.AppField
                                name="Password"
                                children={field => (
                                    <field.Label>
                                        <field.Label.Title label={t('dashboard.users.table.password')} />
                                        {/* TODO: input fields component icon in front not implemented */}
                                        <field.Input
                                            disabled
                                            placeholder="********"
                                        />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            >

                            </form.AppField>
                            <div className="flex justify-end">
                                <Button
                                    onClick={() => setIsPasswordChangeModalOpen(true)}
                                    variant="ghost"
                                    className="font-semibold text-sm"
                                >
                                    {t('dashboard.edit')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                { isPasswordChangeModalOpen
                    ? (
                            <PasswordChangeModal isOpen={isPasswordChangeModalOpen} setIsOpen={setIsPasswordChangeModalOpen} />

                        )
                    : null }
            </div>
        </div>
    );
}
